import invalid from '@xero/xui-icon/icons/invalid';
import XUIButton from '@xero/xui/react/button';
import XUIIcon from '@xero/xui/react/icon';
import { XUIBreadcrumbTrail, XUIPageHeader } from '@xero/xui/react/pageheader';
import { XUIPanel } from '@xero/xui/react/panel';
import { XUITextInput } from '@xero/xui/react/textinput';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { postEvent } from '../../actions';
import ConnectModal from '../../components/ConnectModal';
import FeaturesPanel from '../../components/Features';
import FeaturesComparison from '../../components/FeaturesComparison';
import InformationBanner from '../../components/InformationBanner';
import Overview from '../../components/Overview';
import SalesLayout from '../../components/SalesLayout';
import TaxDisclaimer from '../../components/TaxDisclaimer';
import { validateShopifyHostname } from '../../helpers/validators';
import { bootstrapSelectors } from '../../redux/slices/BootstrapSlice';
import { installationSelectors } from '../../redux/slices/InstallationSlice';
import { userSelectors } from '../../redux/slices/UserSlice';
import { integrationTypes } from '../../static/constants';
import lang from '../../static/lang/en-NZ';
import './Access.css';
import Link from '../../components/Link';
import { translate } from '../../helpers/translate';
import warningIcon from '@xero/xui-icon/icons/warning';
import externalIcon from '@xero/xui-icon/icons/external';
import { organisationSelectors } from '../../redux/slices/OrganisationSlice';
import { convertTimezone } from '../../helpers/date';
import { OrganisationRegion } from '../../static/constants/organisationRegion';
import { isProd } from '../../helpers/env';
import { getFeatureFlagValue } from '../../helpers/getFeatureFlagValue';
import { LDClient } from 'launchdarkly-js-client-sdk';

export interface IAccessProps {
    onConnectClick: (authorizeUrl?: string) => void;
    onPaidConnectClick: () => void;
    showDisconnectedBanner: boolean;
    ldClient?: LDClient;
    flags: any;
}

const getCustomConnectionsAppId = (): string => {
    return isProd()
        ? '3272a43f-40cd-456f-9b0c-2ecdd93f127f' // Prod app id
        : 'c425d652-cd1d-45a8-b4e5-e2a7a1c7ed68'; // UAT app id
};

const Access: FC<IAccessProps> = ({ ldClient, ...props }) => {
    const installation = useSelector(installationSelectors.installation);
    const organisation = useSelector(organisationSelectors.organisation);
    const user = useSelector(userSelectors.user);
    const hasRequiredPlan = useSelector(bootstrapSelectors.hasRequiredPlan);
    const isAdmin = useSelector(bootstrapSelectors.isAdmin);
    const integrationType = installation.integrationType;

    const [isConnecting, setIsConnecting] = useState(false);
    const [isConnectingToPaid, setIsConnectingToPaid] = useState(false);
    const [isHostNameValid, setIsHostNameValid] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [hostName, setHostName] = useState('');
    const [isCustomConnectionsUsTaxEnabled, setIsCustomConnectionsUsTaxEnabled] = useState(
        props.flags?.billedDifferentCustomConnectionsUsTax ?? false
    );
    const [isShopifyNewConnectionsDisabled, setIsShopifyNewConnectionsDisabled] = useState(false);
    const integrationLang: any = lang[integrationType];

    const billing = installation.billing;
    const canBuy = billing?.hasPaidPlan && !billing?.hasBought && isAdmin;
    const hasAccess = isAdmin && hasRequiredPlan;
    const canOnlyGetFreePlan = !billing?.hasPaidPlan && billing?.hasFreePlan;
    const canOnlyGetPaidPlan = billing?.hasPaidPlan && !billing?.hasFreePlan;
    const hasNoPlan = !billing?.hasPaidPlan && !billing?.hasFreePlan;
    // covers scenario where org can start or continue free trial
    const isEligibleForFreeTrial = installation.freeTrial?.isEligibleForTrial && !billing?.hasBought;
    const canStartFreeTrial = isEligibleForFreeTrial && !installation.freeTrial?.freeTrialEndDateUtc;

    const handleShowModal = () => {
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setHostName('');
        setIsHostNameValid(true);
    };

    const handleConfirmButtonAction = () => {
        onConnectClick();
    };

    useEffect(() => {
        document.body.style.background = '#fff';
    }, []);

    useEffect(() => {
        const f = async () => {
            const enabled = await getFeatureFlagValue(
                ldClient!,
                'billed-different-custom-connections-us-tax',
                user.xero_userid,
                {
                    tenantId: organisation.id,
                    tenantRegion: organisation.region!,
                    appId: getCustomConnectionsAppId(),
                }
            );
            setIsCustomConnectionsUsTaxEnabled(enabled ?? false);
        };
        f();
    }, [ldClient, user.xero_userid, organisation.id, organisation.region]);

    useEffect(() => {
        const f = async () => {
            if (installation.integrationType !== integrationTypes.SHOPIFY) {
                setIsShopifyNewConnectionsDisabled(false);
                return;
            }

            const enabled = await getFeatureFlagValue(
                ldClient!,
                'outbound-shopify-integration-decommission-disable-connect-ui',
                user.xero_userid,
                {
                    tenantId: organisation.id,
                    tenantRegion: organisation.region!,
                    integrationType: installation.integrationType,
                }
            );

            setIsShopifyNewConnectionsDisabled(enabled ?? false);
        };
        f();
    }, [ldClient, user.xero_userid, organisation.id, organisation.region, installation.integrationType]);

    const hideShopifyNewConnectionActions =
        installation.integrationType === integrationTypes.SHOPIFY && isShopifyNewConnectionsDisabled;

    const getFeatures = () => {
        if (installation.integrationType === integrationTypes.SHOPIFY) {
            return [];
        }

        if (canOnlyGetPaidPlan || hasNoPlan) {
            // Custom Connections & HubSpot (with paid only enabled)
            return [integrationLang.PAID_PLAN];
        } else if (canOnlyGetFreePlan) {
            // HubSpot in a non app store billing country e.g. US
            return [integrationLang.FREE_PLAN];
        }
        // HubSpot in an app store billing country e.g. NZ (with paid only disabled)
        return [integrationLang.FREE_PLAN, integrationLang.PAID_PLAN_TEMP];
    };

    const getBlurbText = () => {
        if (integrationType === integrationTypes.CUSTOM) {
            if (isCustomConnectionsUsTaxEnabled) {
                return integrationLang.ACCESS_PAGE.APP_STORE_BILLING_BLURB_WITH_US;
            }
        }
        if (hideShopifyNewConnectionActions) {
            return null;
        }
        if (canOnlyGetPaidPlan) {
            return (
                integrationLang.ACCESS_PAGE.PAID_BLURB ??
                integrationLang.ACCESS_PAGE.APP_STORE_BILLING_BLURB ??
                integrationLang.ACCESS_PAGE.BLURB ??
                getBlurb()
            );
        } else if (canOnlyGetFreePlan) {
            return (
                integrationLang.ACCESS_PAGE.FREE_BLURB ??
                integrationLang.ACCESS_PAGE.APP_STORE_BILLING_BLURB ??
                integrationLang.ACCESS_PAGE.BLURB ??
                getBlurb()
            );
        }
        return integrationLang.ACCESS_PAGE.APP_STORE_BILLING_BLURB ?? integrationLang.ACCESS_PAGE.BLURB ?? getBlurb();
    };

    const getBlurb = () => {
        return (
            <div>
                {canStartFreeTrial
                    ? integrationLang.ACCESS_PAGE.FREE_TRIAL_BLURB
                    : integrationLang.ACCESS_PAGE.CONNECT_BLURB}
                <p>
                    {canStartFreeTrial
                        ? integrationLang.ACCESS_PAGE.FREE_TRIAL_SIGNUP_BLURB
                        : integrationLang.ACCESS_PAGE.CONNECT_SIGNUP_BLURB}
                    <br />
                    <Link displayLinkIcon={true} href={installation.signUpUrl}>
                        {integrationLang.ACCESS_PAGE.SIGNUP_LINK_TEXT}
                    </Link>
                </p>
            </div>
        );
    };

    const getLegalText = () => {
        if (canOnlyGetPaidPlan) {
            return integrationLang.PAID_LEGAL_TEXT ?? integrationLang.LEGAL_TEXT;
        } else if (canOnlyGetFreePlan) {
            return integrationLang.FREE_LEGAL_TEXT ?? integrationLang.LEGAL_TEXT;
        }
        return integrationLang.LEGAL_TEXT;
    };

    const onConnectClick = async () => {
        setIsConnecting(true);

        if (installation.requiresHostname) {
            if (installation.integrationType === integrationTypes.SHOPIFY && !validateShopifyHostname(hostName)) {
                setIsHostNameValid(false);
                setIsConnecting(false);

                return;
            }

            setIsHostNameValid(true);
            props.onConnectClick(installation.authorizeUrl!.replace('%s', hostName.trim().replace(/\/$/, '')));

            return;
        }

        props.onConnectClick(installation.authorizeUrl);
    };

    const onPaidConnectClick = async () => {
        setIsConnectingToPaid(true);

        props.onPaidConnectClick();
    };

    const onSignUpClick = () => {
        window.open(installation.signUpUrl);
    };

    const onUpgradeClick = async () => {
        try {
            await postEvent(user, installation, 'upgrade-plan');
        } catch {
            // Do nothing to not break ux
        }

        window.location.href = lang.URLS.PRICING;
    };

    const onHostnameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.replace(/^https?\:\/\//i, '');

        setHostName(value);
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.stopPropagation();
            onConnectClick();
        }
    };

    const onManageSubscriptionClick = (): void => {
        window.open(billing?.manageSubscriptionUrl!);
    };

    const actions = [];
    let salesLayout;

    const smartListsBanner = installation.allowSmartLists === false && (
        <InformationBanner
            bannerMessage={
                <span>
                    {integrationLang.SMART_LISTS_RESTRICTION_BLURB_PART_1}
                    <a href={integrationLang.SMART_LISTS_RESTRICTION_ABSOLUTE_URL}>
                        {integrationLang.SMART_LISTS_RESTRICTION_DISPLAY_URL}
                    </a>
                    {integrationLang.SMART_LISTS_RESTRICTION_BLURB_PART_2}
                </span>
            }
            sentiment={'negative'}
        />
    );

    const activeSubscriptionInstallationDisconnectedBanner = installation.billing?.manageSubscriptionUrl &&
        installation.billing?.nextBillingDate &&
        !installation.billing?.subscriptionEndDate && (
            <div
                className='xui-margin-horizontal'
                data-testid={'access-activeSubscriptionInstallationDisconnected-banner'}
            >
                <InformationBanner
                    header={
                        <>
                            <div className='xui-heading-medium manage-subscription-header'>
                                <XUIIcon icon={warningIcon} color='black' className='xui-margin-right-small' />
                                {translate(lang.INSTALLATION_DISCONNECTED_SUBSCRIPTION_ACTIVE_BANNER.HEADING, {
                                    integrationName: integrationLang.DISPLAY_NAME,
                                })}
                            </div>
                        </>
                    }
                    bannerMessage={lang.INSTALLATION_DISCONNECTED_SUBSCRIPTION_ACTIVE_BANNER.CONTENT}
                    sentiment={'neutral'}
                    action={
                        <XUIButton
                            size='small'
                            variant='borderless-main'
                            qaHook='manage-subscription-button'
                            onClick={onManageSubscriptionClick}
                        >
                            {lang.BUTTONS.MANAGE_SUBSCRIPTION} <XUIIcon icon={externalIcon} />
                        </XUIButton>
                    }
                />
            </div>
        );

    const cancelledSubscriptionInstallationDisconnectedBanner =
        installation.billing?.manageSubscriptionUrl &&
        installation.billing?.subscriptionEndDate &&
        (() => {
            const qaHook = 'access-cancelledSubscriptionInstallationDisconnected-banner';

            const convertedSubscriptionEndDate = convertTimezone(
                new Date(installation.billing.subscriptionEndDate),
                { month: 'short', day: '2-digit', year: 'numeric' },
                organisation.timezone
            );

            const bannerMessage =
                lang.INSTALLATION_DISCONNECTED_SUBSCRIPTION_CANCELLED_BANNER.CONTENT(convertedSubscriptionEndDate);

            return (
                <div className='xui-margin-horizontal' data-testid={qaHook}>
                    <InformationBanner
                        header={
                            <>
                                <div className='xui-heading-medium manage-subscription-header'>
                                    <XUIIcon icon={warningIcon} color='black' className='xui-margin-right-small' />
                                    {translate(lang.INSTALLATION_DISCONNECTED_SUBSCRIPTION_CANCELLED_BANNER.HEADING, {
                                        integrationName: integrationLang.DISPLAY_NAME,
                                    })}
                                </div>
                            </>
                        }
                        bannerMessage={bannerMessage}
                        sentiment={'neutral'}
                        action={
                            <XUIButton
                                size='small'
                                variant='borderless-main'
                                qaHook='manage-subscription-button'
                                onClick={onManageSubscriptionClick}
                            >
                                {lang.BUTTONS.MANAGE_SUBSCRIPTION} <XUIIcon icon={externalIcon} />
                            </XUIButton>
                        }
                    />
                </div>
            );
        })();

    if (
        !hideShopifyNewConnectionActions &&
        billing &&
        !billing.hasBought &&
        !isEligibleForFreeTrial &&
        (canBuy || canOnlyGetPaidPlan || hasNoPlan)
    ) {
        const buttonText = canOnlyGetPaidPlan
            ? (integrationLang?.BUTTONS?.CONNECT_APP ?? lang.BUTTONS.CONNECT_APP)
            : (integrationLang?.PAID_PLAN?.BUTTON ?? lang.BUTTONS.CONNECT_APP);

        actions.push(
            <XUIButton
                key='access-buy-premium'
                qaHook='access-buy-premium'
                className='xui-button-fullwidth xui-margin-top-2xlarge'
                onClick={installation.requiresHostname ? handleShowModal : onPaidConnectClick}
                isLoading={installation.requiresHostname && showModal ? false : isConnectingToPaid}
                loadingAriaLabel='Loading'
                isDisabled={hasNoPlan}
                variant='main'
            >
                {buttonText}
            </XUIButton>
        );
    } else if (
        !hideShopifyNewConnectionActions &&
        billing &&
        (hasAccess || billing.hasBought || isEligibleForFreeTrial)
    ) {
        const buttonText = canStartFreeTrial
            ? lang.BUTTONS.START_FREE_TRIAL
            : (integrationLang?.BUTTONS?.CONNECT_APP ?? lang.BUTTONS.CONNECT_APP);

        actions.push(
            <XUIButton
                key='access-connect'
                qaHook='access-connect'
                className='xui-button-fullwidth xui-margin-top-2xlarge'
                onClick={installation.requiresHostname ? handleShowModal : onConnectClick}
                isLoading={installation.requiresHostname && showModal ? false : isConnecting}
                loadingAriaLabel='Loading'
                variant='main'
            >
                {buttonText}
            </XUIButton>
        );
    } else if (!hideShopifyNewConnectionActions) {
        salesLayout = <SalesLayout isAdmin={isAdmin} onUpgradeClick={onUpgradeClick} />;
    }

    if (!hideShopifyNewConnectionActions && billing && canBuy && !canOnlyGetPaidPlan) {
        actions.push(
            <XUIButton
                key='access-connect'
                qaHook='access-connect'
                className='xui-button-fullwidth xui-margin-top'
                onClick={onConnectClick}
                isLoading={isConnecting}
                loadingAriaLabel='Loading'
            >
                {integrationLang.FREE_PLAN.BUTTON}
            </XUIButton>
        );
    }

    if (
        !hideShopifyNewConnectionActions &&
        installation?.signUpUrl?.length > 0 &&
        installation.integrationType !== 'SHOPIFY'
    ) {
        actions.push(
            <div key='access-signup-url' className='xui-margin-top xui-text-align-center'>
                <a className='text-decoration-underline' onClick={onSignUpClick}>
                    {lang.BUTTONS.APP_SIGNUP.replace('integrationName', installation.displayName).replace(
                        ' Integration',
                        ''
                    )}
                </a>
            </div>
        );
    }

    if (!hideShopifyNewConnectionActions && integrationLang.ACCESS_PAGE.FREE_TRIAL_DETAILS && canStartFreeTrial) {
        actions.push(
            <div key='start-free-trial-details' className='start-free-trial-details xui-textcolor-muted xui-margin-top'>
                {integrationLang.ACCESS_PAGE.FREE_TRIAL_DETAILS}
            </div>
        );
    }

    if (integrationLang.LEGAL_TEXT && !hasNoPlan) {
        actions.push(
            <div key='access-legal-text' className='legal-text xui-textcolor-muted xui-margin-top xui-padding-top'>
                {getLegalText()}
            </div>
        );
    }

    if (hasNoPlan) {
        actions.push(
            <div key='unavailable-in-region-text' className='xui-textcolor-muted xui-padding-top-3xlarge'>
                <div className='xui-column-1-of-12'>
                    <XUIIcon icon={invalid} color='black-faint' />
                </div>
                <div className='xui-column-11-of-12'>{lang.INFORMATION.APP_UNAVAILABLE_IN_REGION}</div>
            </div>
        );
    }

    if (integrationLang.ACCESS_PAGE.TAX_DISCLAIMER) {
        actions.push(
            <div className='content xui-padding-top-large xui-margin-top-large'>
                <TaxDisclaimer blurb={integrationLang.ACCESS_PAGE.TAX_DISCLAIMER} showIcon={true} />
            </div>
        );
    }

    const hostNameInput = !hideShopifyNewConnectionActions && installation.requiresHostname && (
        <ConnectModal
            cancelButtonText={integrationLang.ACCESS_PAGE.MODAL_CANCEL_BUTTON}
            close={handleCloseModal}
            confirmButtonText={integrationLang.ACCESS_PAGE.MODAL_CONNECT_BUTTON}
            confirmButtonAction={handleConfirmButtonAction}
            isLoading={isConnecting}
            show={showModal}
            title={integrationLang.ACCESS_PAGE.MODAL_TITLE}
        >
            {integrationLang.ACCESS_PAGE.MODAL_BODY}
            <div className='xui-margin-top-xlarge'>
                <XUITextInput
                    focusOnMount={true}
                    labelId='hostName'
                    label={integrationLang.ACCESS_PAGE.MODAL_INPUT_HOSTNAME_LABEL}
                    placeholder={integrationLang.ACCESS_PAGE.MODAL_INPUT_HOSTNAME_PLACEHOLDER}
                    onKeyDown={onKeyDown}
                    onChange={onHostnameChange}
                    isInvalid={!isHostNameValid}
                    value={hostName}
                    validationMessage={integrationLang.ACCESS_PAGE.MODAL_INPUT_HOSTNAME_VALIDATION_ERROR}
                    inputProps={{ autoComplete: 'off' }}
                />
            </div>
        </ConnectModal>
    );

    const disconnectedBanner = props.showDisconnectedBanner && (
        <div className='xui-margin-horizontal'>
            <InformationBanner
                qaHook='disconnection-success-banner'
                bannerMessage={translate(lang.DISCONNECTED_BANNER.CONTENT, {
                    integrationName: installation.displayName,
                })}
                displayCloseButton={true}
                sentiment={'positive'}
            />
        </div>
    );

    return (
        <>
            <XUIPageHeader
                breadcrumb={
                    <XUIBreadcrumbTrail breadcrumbs={[{ key: 'a', label: lang.HEADINGS.APPS, href: lang.URLS.APPS }]} />
                }
                title={installation.displayName}
            />
            <XUIPanel
                qaHook='access-page'
                className='access-panel xui-page-width-standard xui-u-flex xui-u-flex-column xui-padding-horizontal-3xlarge xui-padding-bottom-5xlarge xui-margin-top-3xlarge'
            >
                {disconnectedBanner}
                {activeSubscriptionInstallationDisconnectedBanner}
                {cancelledSubscriptionInstallationDisconnectedBanner}
                {isAdmin ? (
                    <Overview
                        title={integrationLang.DISPLAY_NAME}
                        type={installation.integrationType}
                        tags={integrationLang.TAGS}
                        blurb={getBlurbText()}
                        actions={
                            salesLayout ? undefined : (
                                <>
                                    {actions.map((e, i) => (
                                        <e.type key={i} {...e.props} />
                                    ))}
                                </>
                            )
                        }
                    >
                        {smartListsBanner}
                        {
                            <FeaturesPanel
                                featureInfo={
                                    organisation.region?.toUpperCase() === OrganisationRegion.AU
                                        ? integrationLang.ACCESS_PAGE.AU_SUPPORTED_FEATURES
                                        : integrationLang.ACCESS_PAGE.SUPPORTED_FEATURES
                                }
                                isSupported={true}
                            />
                        }
                        {<FeaturesComparison planDescriptions={getFeatures().filter((el) => el != null)} />}
                        {
                            <FeaturesPanel
                                featureInfo={integrationLang.ACCESS_PAGE.UNSUPPORTED_FEATURES}
                                isSupported={false}
                            />
                        }
                        {salesLayout}
                        {hostNameInput}
                    </Overview>
                ) : (
                    <div>{salesLayout}</div>
                )}
            </XUIPanel>
        </>
    );
};

export default withLDConsumer()(Access);
